import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Wrench, Image as ImageIcon, FileText, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { ORDER_TYPE, fmtDate, relDay, todayISO } from "@/lib/format";
import { OrderStatus, Chip } from "@/components/drift/StatusBadge";
import { PageHeader, Panel, EmptyState, LoadingRows, Field } from "@/components/drift/common";
import { NewOrderDialog } from "@/components/drift/Dialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const INST_TYPES = ["Hovudtavle", "Fordeling", "Sikringsskap", "Brannalarm", "Naudlys", "Ladeanlegg", "SD/automasjon", "PLS", "Frekvensomformarar", "Varmekablar", "Belysning", "Tele/data", "Alarm", "Nødstraum/UPS", "Anna"];

function InstallationDialog({ locationId, open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ type: "Hovudtavle", name: "", installed_year: "", manufacturer: "", notes: "", next_control: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Gje installasjonen eit namn"); return; }
    setBusy(true);
    try {
      await api.post("/drift/installations", { ...f, location_id: locationId, installed_year: f.installed_year ? Number(f.installed_year) : null, next_control: f.next_control || null });
      toast.success("Installasjon lagt til");
      onOpenChange(false);
      setF({ type: "Hovudtavle", name: "", installed_year: "", manufacturer: "", notes: "", next_control: "" });
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-installation-dialog">
        <DialogHeader><DialogTitle className="font-display">Ny installasjon</DialogTitle><DialogDescription>Registrer eit anleggselement vi har ansvar for.</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Type"><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="new-installation-type-select"><SelectValue /></SelectTrigger><SelectContent>{INST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Installert år"><Input type="number" value={f.installed_year} onChange={(e) => setF({ ...f, installed_year: e.target.value })} placeholder="2024" data-testid="new-installation-year-input" /></Field>
          <Field label="Namn / modell" className="sm:col-span-2"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="T.d. HT-01 630A" data-testid="new-installation-name-input" /></Field>
          <Field label="Produsent"><Input value={f.manufacturer} onChange={(e) => setF({ ...f, manufacturer: e.target.value })} /></Field>
          <Field label="Neste kontroll"><Input type="date" value={f.next_control} onChange={(e) => setF({ ...f, next_control: e.target.value })} /></Field>
          <Field label="Merknad" className="sm:col-span-2"><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button><Button onClick={submit} disabled={busy} className="bg-brand text-white hover:bg-brand-dark" data-testid="new-installation-submit-button">{busy ? "Lagrar …" : "Legg til"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LocationDetail() {
  const { id } = useParams();
  const [loc, setLoc] = useState(null);
  const [err, setErr] = useState(false);
  const [instOpen, setInstOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);

  const load = useCallback(() => api.get(`/drift/locations/${id}`).then((r) => setLoc(r.data)).catch(() => setErr(true)), [id]);
  useEffect(() => { load(); }, [load]);

  const removeInst = async (iid) => {
    if (!window.confirm("Slette installasjonen?")) return;
    try { await api.delete(`/drift/installations/${iid}`); toast.success("Sletta"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (err) return <EmptyState title="Fann ikkje anlegget" action={<Button variant="outline" asChild><Link to="/drift/kundar">Tilbake</Link></Button>} />;
  if (!loc) return <LoadingRows n={8} />;

  const today = todayISO();
  const images = loc.files.filter((f) => f.content_type?.startsWith("image/"));
  const docs = loc.files.filter((f) => !f.content_type?.startsWith("image/"));

  return (
    <div data-testid="location-detail-page">
      <Link to={`/drift/kundar/${loc.customer_id}`} className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> {loc.customer?.name}</Link>
      <PageHeader eyebrow="Anlegg" title={loc.name} subtitle={`${loc.address}${loc.place ? ` · ${loc.place}` : ""}${loc.description ? ` — ${loc.description}` : ""}`}
        actions={<>
          <Button variant="outline" onClick={() => setInstOpen(true)} data-testid="location-new-installation-button"><Plus className="mr-1.5 h-4 w-4" /> Installasjon</Button>
          <Button onClick={() => setOrderOpen(true)} className="bg-brand text-white hover:bg-brand-dark" data-testid="location-new-order-button"><Plus className="mr-1.5 h-4 w-4" /> Nytt oppdrag</Button>
        </>} />

      <div className="grid gap-4 xl:grid-cols-5">
        <div className="space-y-4 xl:col-span-3">
          <Panel title={`Installasjonar · ${loc.installations.length}`} bodyClass="p-0" testid="location-installations-panel">
            {loc.installations.length === 0 ? <div className="p-4"><EmptyState icon={Wrench} title="Ingen installasjonar registrert" text="Legg til tavler, brannalarm, ladeanlegg osb. for å byggje kunnskapsbasen." /></div> : (
              <table className="w-full text-sm">
                <thead className="bg-accent/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground"><tr><th className="px-4 py-2.5 font-medium">Installasjon</th><th className="hidden px-4 py-2.5 font-medium md:table-cell">Produsent</th><th className="px-4 py-2.5 font-medium">År</th><th className="hidden px-4 py-2.5 font-medium sm:table-cell">Siste service</th><th className="px-4 py-2.5 font-medium">Neste kontroll</th><th className="px-2"></th></tr></thead>
                <tbody className="divide-y divide-border">
                  {loc.installations.map((i) => {
                    const overdue = i.next_control && i.next_control < today;
                    const soon = i.next_control && !overdue && i.next_control <= today.slice(0, 8) + "99";
                    return (
                      <tr key={i.id} data-testid="installation-row">
                        <td className="px-4 py-3"><p className="font-medium">{i.type}</p><p className="text-xs text-muted-foreground">{i.name}{i.notes ? ` — ${i.notes}` : ""}</p></td>
                        <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">{i.manufacturer || "–"}</td>
                        <td className="px-4 py-3 font-mono tabular">{i.installed_year || "–"}</td>
                        <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell">{i.last_service ? fmtDate(i.last_service) : "–"}</td>
                        <td className={cn("px-4 py-3", overdue && "text-danger", soon && "text-warn")}>{i.next_control ? fmtDate(i.next_control) : "–"}{overdue && <span className="ml-1.5 font-mono text-[10px] uppercase">forfalle</span>}</td>
                        <td className="px-2"><button onClick={() => removeInst(i.id)} className="text-muted-foreground hover:text-red-600 dark:hover:text-danger" aria-label="Slett" data-testid="installation-delete-button"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Panel>

          <Panel title="Dokumentasjon og bilete" testid="location-files-panel">
            {loc.files.length === 0 ? <p className="text-sm text-muted-foreground">Bilete og dokument frå oppdrag på anlegget dukkar opp her.</p> : (
              <>
                {images.length > 0 && <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">{images.map((f) => <a key={f.id} href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-sm bg-accent"><img src={fileUrl(f.id)} alt={f.filename} className="h-full w-full object-cover" /></a>)}</div>}
                {docs.length > 0 && <ul className="mt-3 divide-y divide-border">{docs.map((f) => <li key={f.id} className="flex items-center gap-2 py-2 text-sm"><FileText className="h-4 w-4 text-muted-foreground" /><a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="truncate hover:underline">{f.filename}</a></li>)}</ul>}
              </>
            )}
          </Panel>
        </div>

        <div className="xl:col-span-2">
          <Panel title="Historikk" bodyClass="p-0" testid="location-timeline-panel">
            {loc.timeline.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Ingen historikk enno.</p> : (
              <ol className="relative">
                {loc.timeline.map((t, i) => (
                  <li key={i} className="relative flex gap-4 px-4 py-3" data-testid="timeline-item">
                    <div className="flex flex-col items-center">
                      <span className={cn("mt-1.5 h-2.5 w-2.5 rounded-full", t.kind === "installasjon" ? "bg-sky-400" : t.status === "fakturert" || t.status === "ferdig" ? "bg-emerald-400" : t.status === "paagaar" ? "bg-orange-400" : "bg-slate-400")} />
                      {i < loc.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-border" />}
                    </div>
                    <div className="min-w-0 flex-1 pb-1">
                      <p className="font-mono text-[11px] text-muted-foreground">{t.kind === "installasjon" ? t.date.slice(0, 4) : fmtDate(t.date)}</p>
                      {t.order_id ? (
                        <Link to={`/drift/oppdrag/${t.order_id}`} className="mt-0.5 block text-sm font-medium hover:underline">{t.title}</Link>
                      ) : <p className="mt-0.5 text-sm font-medium">{t.title}</p>}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {t.kind === "installasjon" ? <Chip>Installasjon</Chip> : <><Chip>{ORDER_TYPE[t.type]}</Chip><OrderStatus status={t.status} /></>}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </div>
      </div>

      <InstallationDialog locationId={loc.id} open={instOpen} onOpenChange={setInstOpen} onSaved={load} />
      <NewOrderDialog open={orderOpen} onOpenChange={setOrderOpen} customerId={loc.customer_id} locationId={loc.id} />
    </div>
  );
}
